import React, { useState, memo } from 'react';
import { Box, Tooltip, Text, Flex, Badge, IconButton, HStack } from '@chakra-ui/react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { MapTrifold, MapPin, SquaresFour } from 'phosphor-react';
import {
  IEmpresaPorUf,
  IEmpresaPorMunicipio,
  IPorcentagemUf,
  IPorcentagemMunicipio,
} from '../../hooks/useEmpresaDash';

// URL do TopoJSON do Brasil (estados)
const BRAZIL_TOPO_JSON =
  'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson';

interface BrazilMapProps {
  data: IEmpresaPorUf[];
  municipioData?: IEmpresaPorMunicipio[];
  porcentagemUf?: IPorcentagemUf[];
  porcentagemMunicipio?: IPorcentagemMunicipio[];
  selectedUf?: string | null;
  // eslint-disable-next-line no-unused-vars
  onStateClick?: (uf: string | null) => void;
}

// Mapeamento de siglas para nomes
const stateNames: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AM: 'Amazonas',
  AP: 'Amapá',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MG: 'Minas Gerais',
  MS: 'Mato Grosso do Sul',
  MT: 'Mato Grosso',
  PA: 'Pará',
  PB: 'Paraíba',
  PE: 'Pernambuco',
  PI: 'Piauí',
  PR: 'Paraná',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RO: 'Rondônia',
  RR: 'Roraima',
  RS: 'Rio Grande do Sul',
  SC: 'Santa Catarina',
  SE: 'Sergipe',
  SP: 'São Paulo',
  TO: 'Tocantins',
};

// Nova paleta de cores: degradê de azul claro (baixo) para roxo (alto)
const getStateColor = (sigla: string, data: IEmpresaPorUf[]): string => {
  const stateData = data.find(item => item.uf === sigla);
  if (!stateData || stateData.quantidade === 0) return '#E2E8F0';

  const validData = data.filter(d => d.uf && d.quantidade > 0);
  if (validData.length === 0) return '#E2E8F0';

  const maxQnt = Math.max(...validData.map(d => d.quantidade));
  if (maxQnt === 0) return '#E2E8F0';

  const intensity = stateData.quantidade / maxQnt;

  // Degradê: azul claro -> azul escuro -> roxo -> roxo escuro
  if (intensity > 0.8) return '#581C87'; // purple-900 - Muito Alta
  if (intensity > 0.6) return '#7C3AED'; // violet-600 - Alta
  if (intensity > 0.4) return '#1E3A8A'; // blue-900 - Média (azul escuro)
  if (intensity > 0.15) return '#93C5FD'; // blue-300 - Baixa
  return '#BFDBFE'; // blue-200 - Sem dados (quando tem valor mas muito baixo)
};

type ViewMode = 'mapa' | 'estados' | 'municipios';

const getEstadosTransform = (viewMode: ViewMode): string => {
  if (viewMode === 'estados') return 'translateX(0)';
  if (viewMode === 'mapa') return 'translateX(100%)';
  return 'translateX(-100%)';
};

const BrazilMapComponent: React.FC<BrazilMapProps> = ({
  data,
  municipioData = [],
  porcentagemUf = [],
  porcentagemMunicipio = [],
  selectedUf,
  onStateClick,
}) => {
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('mapa');
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  const getTooltipContent = (sigla: string) => {
    const stateData = data.find(item => item.uf === sigla);
    const stateName = stateNames[sigla] || sigla;
    const quantidade = stateData?.quantidade || 0;
    return `${stateName}: ${quantidade} empresa${quantidade !== 1 ? 's' : ''}`;
  };

  // Helpers para buscar percentuais
  const getUfPercent = (uf: string): number | null => {
    const found = porcentagemUf.find(u => u.uf === uf);
    return found ? found.percentual : null;
  };

  const getMunicipioPercent = (uf: string, cidade: string): number | null => {
    const found = porcentagemMunicipio.find(m => m.uf === uf && m.cidade === cidade);
    return found ? found.percentual : null;
  };

  // Ordenar municípios e estados por quantidade
  const sortedMunicipios = [...municipioData]
    .filter(m => m.cidade)
    .sort((a, b) => b.quantidade - a.quantidade);

  const sortedEstados = [...data].filter(e => e.uf).sort((a, b) => b.quantidade - a.quantidade);

  return (
    <Box position="relative" width="100%">
      {/* Toggle Mapa/Estados/Municípios */}
      <Flex justify="flex-end" mb={3}>
        <HStack spacing={1} bg="gray.100" p={1} borderRadius="lg">
          <Tooltip label="Mapa" hasArrow>
            <IconButton
              aria-label="Ver mapa"
              icon={<MapTrifold size={18} />}
              size="sm"
              variant={viewMode === 'mapa' ? 'solid' : 'ghost'}
              colorScheme={viewMode === 'mapa' ? 'purple' : 'gray'}
              onClick={() => setViewMode('mapa')}
            />
          </Tooltip>
          <Tooltip label="Estados" hasArrow>
            <IconButton
              aria-label="Ver estados"
              icon={<SquaresFour size={18} />}
              size="sm"
              variant={viewMode === 'estados' ? 'solid' : 'ghost'}
              colorScheme={viewMode === 'estados' ? 'purple' : 'gray'}
              onClick={() => setViewMode('estados')}
            />
          </Tooltip>
          <Tooltip label="Municípios" hasArrow>
            <IconButton
              aria-label="Ver municípios"
              icon={<MapPin size={18} />}
              size="sm"
              variant={viewMode === 'municipios' ? 'solid' : 'ghost'}
              colorScheme={viewMode === 'municipios' ? 'purple' : 'gray'}
              onClick={() => setViewMode('municipios')}
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Container com animação de carrossel */}
      <Box position="relative" overflow="hidden" minH="480px">
        {/* View Mapa */}
        <Box
          position={viewMode === 'mapa' ? 'relative' : 'absolute'}
          top={0}
          left={0}
          width="100%"
          opacity={viewMode === 'mapa' ? 1 : 0}
          transform={viewMode === 'mapa' ? 'translateX(0)' : 'translateX(-100%)'}
          transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          pointerEvents={viewMode === 'mapa' ? 'auto' : 'none'}
        >
          <Tooltip
            label={tooltipContent}
            isOpen={tooltipVisible && !!tooltipContent}
            placement="top"
            hasArrow
            bg="purple.700"
            color="white"
            fontSize="sm"
            fontWeight="medium"
            px={3}
            py={2}
            borderRadius="md"
          >
            <Box>
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                  scale: 750,
                  center: [-54, -15],
                }}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '420px',
                }}
              >
                {/* Pattern de hachura para hover */}
                <defs>
                  <pattern
                    id="hachura-hover"
                    patternUnits="userSpaceOnUse"
                    width="8"
                    height="8"
                    patternTransform="rotate(45)"
                  >
                    <line x1="0" y1="0" x2="0" y2="8" stroke="#FCD34D" strokeWidth="3" />
                  </pattern>
                </defs>
                <Geographies geography={BRAZIL_TOPO_JSON}>
                  {({ geographies }) =>
                    geographies.map(geo => {
                      const sigla = geo.properties.sigla || geo.properties.UF || '';
                      const isSelected = selectedUf === sigla;
                      const isHovered = hoveredState === sigla && !isSelected;
                      const baseColor = getStateColor(sigla, data);

                      return (
                        <g key={geo.rsmKey}>
                          {/* Camada base com a cor do estado */}
                          <Geography
                            geography={geo}
                            fill={isSelected ? '#4C1D95' : baseColor}
                            stroke={isSelected || isHovered ? '#FCD34D' : '#7C3AED'}
                            strokeWidth={isSelected || isHovered ? 2 : 0.5}
                            style={{
                              default: { outline: 'none' },
                              hover: { outline: 'none', cursor: 'pointer' },
                              pressed: { outline: 'none' },
                            }}
                            onMouseEnter={() => {
                              setHoveredState(sigla);
                              // Só mostra tooltip se não há estado selecionado ou se é o estado selecionado
                              if (!selectedUf || selectedUf === sigla) {
                                setTooltipContent(getTooltipContent(sigla));
                                setTooltipVisible(true);
                              }
                            }}
                            onMouseLeave={() => {
                              setHoveredState(null);
                              setTooltipContent('');
                              setTooltipVisible(false);
                            }}
                            onClick={() => {
                              if (onStateClick && sigla) {
                                // Se já está selecionado, remove o filtro; senão, aplica
                                onStateClick(selectedUf === sigla ? null : sigla);
                              }
                            }}
                          />
                          {/* Camada de hachura por cima quando hover */}
                          {isHovered && (
                            <Geography
                              geography={geo}
                              fill="url(#hachura-hover)"
                              stroke="none"
                              style={{
                                default: { outline: 'none', pointerEvents: 'none' },
                                hover: { outline: 'none', pointerEvents: 'none' },
                                pressed: { outline: 'none', pointerEvents: 'none' },
                              }}
                            />
                          )}
                        </g>
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </Box>
          </Tooltip>

          {/* Legenda do Mapa */}
          <Box mt={3} mx="auto" width="fit-content">
            <Flex gap={2} flexWrap="wrap" justify="center">
              <Flex alignItems="center" gap={1}>
                <Box w="12px" h="12px" bg="#581C87" borderRadius="sm" />
                <Text fontSize="xs">Muito Alta</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="12px" h="12px" bg="#7C3AED" borderRadius="sm" />
                <Text fontSize="xs">Alta</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="12px" h="12px" bg="#1E3A8A" borderRadius="sm" />
                <Text fontSize="xs">Média</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="12px" h="12px" bg="#93C5FD" borderRadius="sm" />
                <Text fontSize="xs">Baixa</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w="12px" h="12px" bg="#E2E8F0" borderRadius="sm" />
                <Text fontSize="xs">Sem dados</Text>
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/* View Estados (Grid) */}
        <Box
          position={viewMode === 'estados' ? 'relative' : 'absolute'}
          top={0}
          left={0}
          width="100%"
          opacity={viewMode === 'estados' ? 1 : 0}
          transform={getEstadosTransform(viewMode)}
          transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          pointerEvents={viewMode === 'estados' ? 'auto' : 'none'}
        >
          <Box
            maxH="470px"
            overflowY="auto"
            pr={2}
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#A78BFA',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#7C3AED',
              },
            }}
          >
            <Flex flexWrap="wrap" gap={3} justify="center">
              {sortedEstados.map((estado, index) => {
                const maxQnt = sortedEstados[0]?.quantidade || 1;
                const intensity = estado.quantidade / maxQnt;
                const percent = estado.uf ? getUfPercent(estado.uf) : null;
                let bgColor = 'blue.50';
                let textColor = 'blue.600';

                if (intensity > 0.8) {
                  bgColor = 'purple.100';
                  textColor = 'purple.700';
                } else if (intensity > 0.5) {
                  bgColor = 'violet.50';
                  textColor = 'violet.600';
                } else if (intensity > 0.3) {
                  bgColor = 'blue.100';
                  textColor = 'blue.600';
                }

                return (
                  <Box
                    key={estado.uf || `estado-${index}`}
                    p={4}
                    bg={bgColor}
                    borderRadius="lg"
                    textAlign="center"
                    minW="70px"
                    transition="all 0.2s"
                    _hover={{ transform: 'scale(1.05)', shadow: 'md' }}
                  >
                    {index < 3 && (
                      <Badge colorScheme="yellow" variant="solid" fontSize="xs" mb={1}>
                        #{index + 1}
                      </Badge>
                    )}
                    <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                      {estado.quantidade}
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      {estado.uf || 'N/A'}
                    </Text>
                    {percent !== null && (
                      <Text fontSize="xs" color="gray.500">
                        {percent.toFixed(1)}%
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>

        {/* View Municípios (Lista) */}
        <Box
          position={viewMode === 'municipios' ? 'relative' : 'absolute'}
          top={0}
          left={0}
          width="100%"
          opacity={viewMode === 'municipios' ? 1 : 0}
          transform={viewMode === 'municipios' ? 'translateX(0)' : 'translateX(100%)'}
          transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          pointerEvents={viewMode === 'municipios' ? 'auto' : 'none'}
        >
          <Box
            maxH="450px"
            overflowY="auto"
            pr={2}
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#A78BFA',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#7C3AED',
              },
            }}
          >
            <Flex direction="column" gap={2}>
              {sortedMunicipios.map((mun, index) => {
                const maxQnt = sortedMunicipios[0]?.quantidade || 1;
                const intensity = mun.quantidade / maxQnt;
                const percent =
                  mun.uf && mun.cidade ? getMunicipioPercent(mun.uf, mun.cidade) : null;
                let bgColor = 'blue.50';
                let borderColor = 'blue.200';

                if (intensity > 0.8) {
                  bgColor = 'purple.100';
                  borderColor = 'purple.500';
                } else if (intensity > 0.5) {
                  bgColor = 'violet.50';
                  borderColor = 'violet.400';
                } else if (intensity > 0.3) {
                  bgColor = 'blue.50';
                  borderColor = 'blue.400';
                }

                return (
                  <Box
                    key={`${mun.uf}-${mun.cidade}-${mun.quantidade}`}
                    p={3}
                    bg={bgColor}
                    borderRadius="lg"
                    borderLeft="4px solid"
                    borderLeftColor={borderColor}
                    transition="all 0.2s"
                    _hover={{ transform: 'translateX(4px)', shadow: 'sm' }}
                  >
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>
                          {mun.cidade || 'N/A'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {mun.uf || '-'}
                        </Text>
                      </Box>
                      <Flex align="center" gap={2}>
                        {index < 3 && (
                          <Badge colorScheme="yellow" variant="solid" fontSize="xs">
                            #{index + 1}
                          </Badge>
                        )}
                        <Badge colorScheme="purple" variant="subtle" fontSize="sm" px={2}>
                          {mun.quantidade}
                        </Badge>
                        {percent !== null && (
                          <Text fontSize="xs" color="gray.500">
                            {percent.toFixed(1)}%
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const BrazilMap = memo(BrazilMapComponent);
